// src/components/partner/CoBrandBar.jsx
// "Planned by <partner> · Booked and serviced by RouteChef Labs Pvt Ltd".
// RouteChef sells the booking (payment, invoice, ticket, cancellations, support), so the
// seller line is always shown next to the partner's branding.
//
//   <CoBrandBar brand={usePartnerBrand()} />                   full strip (booking pages)
//   <CoBrandBar brand={...} variant="compact" />               small pill (page headers)
//   <CoBrandBar brand={...} context="share" />                 strip wording for shared pages
//   <CoBrandBar brand={...} variant="slim" />                  one line seller note (booking pages)
import React from "react";
import { Phone, ShieldCheck } from "lucide-react";

export const SELLER_NAME = "RouteChef Labs Pvt Ltd";

const INK = "#0F172A";
const MUTED = "#64748B";
const LINE = "#E2E8F0";

function Mark({ brand, size }) {
    if (brand.logo) {
        return (
            <span style={{ ...S.logoBox, height: size, maxWidth: size * 2.6 }}>
                <img src={brand.logo} alt={`${brand.name} logo`} style={{ maxHeight: size - 8, maxWidth: size * 2.6 - 8, objectFit: "contain", display: "block" }} />
            </span>
        );
    }
    return (
        <span style={{ ...S.initial, width: size, height: size, fontSize: size * 0.42 }} aria-hidden="true">
            {(brand.name || "?").trim().charAt(0).toUpperCase()}
        </span>
    );
}

const telHref = (p) => `tel:${String(p || "").replace(/[^\d+]/g, "")}`;

export default function CoBrandBar({ brand, variant = "strip", context = "booking", style }) {
    if (!brand?.name) return null;

    if (variant === "slim") {
        // One line under the header on booking pages: the partner is in the navbar, this names the seller
        return (
            <div style={{ ...S.slim, ...style }} role="note">
                <ShieldCheck size={15} strokeWidth={2.2} style={{ color: "#16A34A", flexShrink: 0 }} />
                <span>
                    {brand.own ? "Shown to your customers: " : ""}Planned by <b style={{ color: INK }}>{brand.name}</b>
                    {brand.phone && <> (<a href={telHref(brand.phone)} style={{ color: "#334155", fontWeight: 600, textDecoration: "none" }}>{brand.phone}</a>)</>}
                    <span style={{ color: "#CBD5E1" }}>{"  |  "}</span>
                    Booked and serviced by <b style={{ color: INK }}>{SELLER_NAME}</b>. Payment, invoice, tickets and cancellations are handled by RouteChef.
                </span>
            </div>
        );
    }

    if (variant === "compact") {
        return (
            <div style={{ ...S.pill, ...style }} title={`Planned by ${brand.name}. Booked and serviced by ${SELLER_NAME}.`}>
                <Mark brand={brand} size={28} />
                <span style={{ minWidth: 0, lineHeight: 1.15 }}>
                    <span style={S.pillKicker}>{brand.own ? "Your branding" : "Planned by"}</span>
                    <span style={S.pillName}>{brand.name}</span>
                </span>
            </div>
        );
    }

    const lead = context === "share" ? "Shared by your travel planner" : "Your travel planner";
    return (
        <section style={{ ...S.strip, ...style }} aria-label="Trip planned by">
            <div style={S.left}>
                <Mark brand={brand} size={44} />
                <div style={{ minWidth: 0 }}>
                    <div style={S.kicker}>{brand.own ? "Your customers see this" : lead}</div>
                    <div style={S.name}>{brand.name}</div>
                    {(brand.phone || brand.email) && (
                        <div style={S.contact}>
                            {brand.phone && (
                                <a href={telHref(brand.phone)} style={S.link}><Phone size={12} strokeWidth={2.4} /> {brand.phone}</a>
                            )}
                            {brand.email && <a href={`mailto:${brand.email}`} style={S.link}>{brand.email}</a>}
                        </div>
                    )}
                </div>
            </div>
            <div style={S.seller}>
                <ShieldCheck size={16} strokeWidth={2.2} style={{ color: "#16A34A", flexShrink: 0, marginTop: 1 }} />
                <span>
                    Booked and serviced by <b style={{ color: INK }}>{SELLER_NAME}</b>.
                    <span style={{ display: "block" }}>Payment, invoice, tickets and cancellations are handled by RouteChef.</span>
                </span>
            </div>
        </section>
    );
}

const S = {
    strip: {
        display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "10px 20px",
        background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, padding: "12px 16px",
        fontFamily: "'Inter', -apple-system, system-ui, sans-serif", boxSizing: "border-box", width: "100%",
    },
    left: { display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 260px" },
    kicker: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 },
    name: { fontSize: 16, fontWeight: 800, color: INK, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    contact: { display: "flex", flexWrap: "wrap", gap: "2px 12px", marginTop: 2 },
    link: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12.5, color: "#334155", textDecoration: "none", fontWeight: 600 },
    seller: { display: "flex", gap: 8, fontSize: 12, color: MUTED, lineHeight: 1.45, flex: "1 1 240px", maxWidth: 380 },
    logoBox: { display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, padding: "0 6px", flexShrink: 0, boxSizing: "border-box" },
    initial: { display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 10, background: "#1E293B", color: "#fff", fontWeight: 800, flexShrink: 0 },
    slim: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: MUTED, background: "#F8FAFC", border: `1px solid ${LINE}`, borderRadius: 10, padding: "8px 12px", fontFamily: "'Inter', -apple-system, system-ui, sans-serif", boxSizing: "border-box", width: "100%" },
    pill: { display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, padding: "4px 10px 4px 4px", maxWidth: 260, fontFamily: "'Inter', -apple-system, system-ui, sans-serif" },
    pillKicker: { display: "block", fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.3 },
    pillName: { display: "block", fontSize: 13.5, fontWeight: 800, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 190 },
};
